import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AdminHeader } from "./AdminLayout";
import API_ROUTES from "../../hooks/apiRoutes";
import { apiGet, useAdminState, Erro } from "../../hooks/useAdminApi";

/**
 * PERFIS DAS CRIANÇAS — o que o funil de entrada coleta.
 *
 * ⚠️ POR QUE OS CAMPOS SENSÍVEIS NASCEM MASCARADOS
 * Esta é a tela mais sensível do painel. `nome` é de uma CRIANÇA; `tradição religiosa` e
 * `relação com Deus` são DADO SENSÍVEL pela LGPD (convicção religiosa, art. 5º/II). O admin
 * TEM direito de ver — é o controlador dos dados —, mas ver não pode ser o padrão: o painel
 * fica aberto em telas compartilhadas, e uma lista de crianças com a religião de cada
 * família escancarada é exatamente o risco que a gente evita. Então:
 *   - os RESUMOS (quantos por idade, por objetivo...) aparecem sempre — são agregados, não
 *     expõem ninguém individualmente;
 *   - na TABELA, nome/tradição/relação com Deus vêm ocultos, e revelar é um clique
 *     CONSCIENTE (o botão diz o que está fazendo).
 */

// slug do app -> rótulo legível. Slugs estáveis (batem com o registry.ts do app e o seed).
const REL_CRIANCA = { mae: "Mãe", pai: "Pai", avo: "Avô/Avó", outro: "Outro" };
const TRADICAO = {
  catolica: "Católica",
  evangelica: "Evangélica",
  nao_denominacional: "Não denominacional",
  outra: "Outra",
};
const REL_DEUS = {
  forte: "Relacionamento forte",
  crescendo: "Crescendo na fé",
  comecando: "Começando a aprender",
  nao_envolvido: "Não envolvido",
};
const OBJETIVO = {
  amar_jesus: "Amar Jesus",
  valores_biblicos: "Valores bíblicos",
  entretenimento_seguro: "Entretenimento seguro",
  habito_diario: "Hábito diário",
  outro: "Outro",
};
const rotulo = (mapa, slug) => (slug == null ? "—" : mapa[slug] || slug);

export default function Perfis() {
  const { tema, setTema } = useOutletContext();
  const { erro, rodar, limparErro } = useAdminState();

  const [dados, setDados] = useState(null);
  const [filtros, setFiltros] = useState({ idadeMin: "", idadeMax: "", relacao: "", religiao: "", objetivo: "" });
  const [page, setPage] = useState(1);
  const [revelar, setRevelar] = useState(false);

  const carregar = useCallback(
    () =>
      rodar(async () => {
        const q = { page };
        for (const [k, v] of Object.entries(filtros)) if (v !== "") q[k] = v;
        setDados(await apiGet(API_ROUTES.ADMIN.profiles(q)));
      }),
    [rodar, filtros, page]
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  const resumo = dados?.resumo;
  const rows = useMemo(() => dados?.rows || [], [dados]);
  const total = dados?.total ?? 0;
  const size = dados?.size ?? 25;
  const totalPaginas = Math.max(1, Math.ceil(total / size));

  const setF = (k, v) => {
    setPage(1);
    setFiltros((f) => ({ ...f, [k]: v }));
  };
  const limpar = () => {
    setPage(1);
    setFiltros({ idadeMin: "", idadeMax: "", relacao: "", religiao: "", objetivo: "" });
  };
  const temFiltro = Object.values(filtros).some((v) => v !== "");

  // mascara um valor sensível quando "revelar" está desligado
  const oculto = (v, formatar = (x) => x) =>
    v == null ? "—" : revelar ? formatar(v) : "•••••";

  return (
    <>
      <AdminHeader
        tema={tema}
        setTema={setTema}
        titulo="Perfis das crianças"
        subtitulo="O que o funil de entrada coleta. Números por cima, a lista embaixo — com os dados sensíveis protegidos."
      />

      <div className="adm__body">
        <Erro erro={erro} onFechar={limparErro} />

        {/* ---------- INSIGHTS (agregado, sempre visível) ---------- */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
          <div className="adm__card" style={{ padding: "16px 22px", minWidth: 150 }}>
            <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 5 }}>
              {temFiltro ? "Perfis no filtro" : "Total de perfis"}
            </div>
            <strong style={{ fontSize: 30, color: "var(--red)" }}>{total}</strong>
          </div>
          <Distribuicao titulo="Por idade" dados={resumo?.porIdade} formatar={(k) => (k === "(não informado)" ? k : `${k} anos`)} />
          <Distribuicao titulo="Relação com a criança" dados={resumo?.porRelacaoCrianca} formatar={(k) => rotulo(REL_CRIANCA, k)} />
          <Distribuicao titulo="Objetivo dos pais" dados={resumo?.porObjetivo} formatar={(k) => rotulo(OBJETIVO, k)} />
          <Distribuicao titulo="Relação com Deus" dados={resumo?.porRelacaoDeus} formatar={(k) => rotulo(REL_DEUS, k)} sensivel />
          <Distribuicao titulo="Tradição religiosa" dados={resumo?.porTradicao} formatar={(k) => rotulo(TRADICAO, k)} sensivel />
        </div>

        {dados?.resumoParcial && (
          <p style={{ fontSize: 12, color: "var(--orange)", margin: "0 0 14px" }}>
            ⚠️ Há muitos perfis — os números acima consideram os primeiros 5.000. A lista abaixo pagina normalmente.
          </p>
        )}

        {/* ---------- FILTROS ---------- */}
        <div className="adm__card" style={{ padding: 14, margin: "14px 0", display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Campo label="Idade mín.">
            <input className="adm__input" style={{ width: 80 }} type="number" min="0" max="24" value={filtros.idadeMin} onChange={(e) => setF("idadeMin", e.target.value)} />
          </Campo>
          <Campo label="Idade máx.">
            <input className="adm__input" style={{ width: 80 }} type="number" min="0" max="24" value={filtros.idadeMax} onChange={(e) => setF("idadeMax", e.target.value)} />
          </Campo>
          <Campo label="Relação com a criança">
            <Select valor={filtros.relacao} onChange={(v) => setF("relacao", v)} mapa={REL_CRIANCA} />
          </Campo>
          <Campo label="Tradição religiosa">
            <Select valor={filtros.religiao} onChange={(v) => setF("religiao", v)} mapa={TRADICAO} />
          </Campo>
          <Campo label="Objetivo">
            <Select valor={filtros.objetivo} onChange={(v) => setF("objetivo", v)} mapa={OBJETIVO} />
          </Campo>
          {temFiltro && (
            <button className="adm__btnGhost" onClick={limpar} style={{ marginBottom: 1 }}>
              Limpar filtros
            </button>
          )}
          <button
            className="adm__btnGhost"
            onClick={() => setRevelar((r) => !r)}
            style={{ marginLeft: "auto", marginBottom: 1, color: revelar ? "var(--orange)" : "var(--text2)", borderColor: revelar ? "rgba(255,140,0,.4)" : "var(--border)" }}
            title="Nome, tradição religiosa e relação com Deus são dados sensíveis de criança"
          >
            {revelar ? "🔓 Ocultar dados sensíveis" : "🔒 Revelar dados sensíveis"}
          </button>
        </div>

        {/* ---------- TABELA ---------- */}
        {total === 0 ? (
          <div className="adm__card" style={{ padding: 22 }}>
            <p className="adm__vazio" style={{ margin: 0 }}>
              {temFiltro
                ? "Nenhum perfil bate esses filtros."
                : "Ainda não há perfis. Eles aparecem aqui quando as famílias concluírem o funil de entrada no app."}
            </p>
          </div>
        ) : (
          <div className="adm__card" style={{ overflow: "hidden" }}>
            <table className="adm__tabela">
              <thead>
                <tr>
                  <th>Nome 🔒</th>
                  <th style={{ width: 70 }}>Idade</th>
                  <th style={{ width: 130 }}>Relação</th>
                  <th style={{ width: 160 }}>Tradição 🔒</th>
                  <th style={{ width: 170 }}>Relação c/ Deus 🔒</th>
                  <th>Objetivos</th>
                  <th style={{ width: 110 }}>Criado</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600 }}>{oculto(p.name)}</td>
                    <td>{p.idade == null ? "—" : `${p.idade}`}</td>
                    <td>{rotulo(REL_CRIANCA, p.caregiverRelation)}</td>
                    <td style={{ color: "var(--text2)" }}>{oculto(p.religion, (v) => rotulo(TRADICAO, v))}</td>
                    <td style={{ color: "var(--text2)" }}>{oculto(p.godRelation, (v) => rotulo(REL_DEUS, v))}</td>
                    <td>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {(p.preferences || []).length
                          ? p.preferences.map((o) => (
                              <span key={o} className="adm__chip" style={{ fontSize: 10.5 }}>
                                {rotulo(OBJETIVO, o)}
                              </span>
                            ))
                          : <span style={{ color: "var(--text3)" }}>—</span>}
                      </div>
                    </td>
                    <td style={{ color: "var(--text3)", fontSize: 12 }}>
                      {p.createdAt ? new Date(p.createdAt).toLocaleDateString("pt-BR") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ---------- PAGINAÇÃO ---------- */}
        {totalPaginas > 1 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, marginTop: 16 }}>
            <button className="adm__btnGhost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              ← Anterior
            </button>
            <span style={{ fontSize: 13, color: "var(--text2)" }}>
              Página {page} de {totalPaginas}
            </span>
            <button className="adm__btnGhost" disabled={page >= totalPaginas} onClick={() => setPage((p) => p + 1)}>
              Próxima →
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function Campo({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <label style={{ fontSize: 11.5, color: "var(--text2)", fontWeight: 500 }}>{label}</label>
      {children}
    </div>
  );
}

function Select({ valor, onChange, mapa }) {
  return (
    <select className="adm__select" style={{ width: 180 }} value={valor} onChange={(e) => onChange(e.target.value)}>
      <option value="">Todas</option>
      {Object.entries(mapa).map(([slug, label]) => (
        <option key={slug} value={slug}>{label}</option>
      ))}
    </select>
  );
}

/** Uma mini-distribuição: barras proporcionais a partir de um {chave: contagem}. */
function Distribuicao({ titulo, dados, formatar = (x) => x, sensivel }) {
  const itens = useMemo(() => {
    const e = Object.entries(dados || {});
    // "(não informado)" por último; o resto por contagem desc
    return e.sort((a, b) => {
      if (a[0] === "(não informado)") return 1;
      if (b[0] === "(não informado)") return -1;
      return b[1] - a[1];
    });
  }, [dados]);
  const max = itens.reduce((m, [, v]) => Math.max(m, v), 0) || 1;

  return (
    <div className="adm__card" style={{ padding: "13px 16px", minWidth: 210, flex: "1 1 210px", maxWidth: 320 }}>
      <div style={{ fontSize: 11.5, color: "var(--text2)", marginBottom: 9, fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}>
        {titulo}
        {sensivel && <span title="Dado sensível — mostrado só de forma agregada, nunca por criança" style={{ color: "var(--text3)" }}>🔒</span>}
      </div>
      {!itens.length ? (
        <div style={{ fontSize: 12, color: "var(--text3)" }}>sem dados ainda</div>
      ) : (
        itens.slice(0, 5).map(([k, v]) => (
          <div key={k} style={{ marginBottom: 6 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, marginBottom: 2 }}>
              <span style={{ color: k === "(não informado)" ? "var(--text3)" : "var(--text)" }}>{formatar(k)}</span>
              <strong style={{ color: "var(--text2)" }}>{v}</strong>
            </div>
            <div style={{ height: 5, borderRadius: 4, background: "var(--elev)" }}>
              <div style={{ height: "100%", width: `${(v / max) * 100}%`, borderRadius: 4, background: k === "(não informado)" ? "var(--text3)" : "var(--red)" }} />
            </div>
          </div>
        ))
      )}
    </div>
  );
}
